/**
 * Contradiction Offcanvas (issue #66)
 *
 * Bootstrap 5 offcanvas that opens when CHECK_RULES finds contradictions
 * and shows each contradiction as a side-by-side comparison of the two
 * conflicting rules with diff-highlighted fields. First option from #66 —
 * comparison-pane approach (no character offsets needed).
 */

import Offcanvas from 'bootstrap/js/src/offcanvas.js';
import { useAppStore } from '../stores/app-store';
import { escapeHtml } from '../core/utilities/dom';
import { formatRequirement } from '../core/utilities/formatting';
import { findAndFlashStatement } from '../core/utilities/document-rule-locator';
import ToastService from './toast-service';
import { BaseComponent } from './base-component';
import { logger } from '../core/logger';
import type {
  CheckRulesResponse,
  StatementDisplay,
  ConflictDisplay,
  NearMissDisplay,
} from '../core/gateway-service';
import { interactionLabels } from '../core/relations/interaction-labels';
import {
  createResolveLabel,
  buildConflictMessage,
  buildSimilarityMessage,
  buildGapMessage,
  type MessageContext,
  type InteractionMessage,
} from '../core/relations/interaction-messages';

/**
 * Offcanvas behaviour:
 *  - `backdrop: false` — no darkening overlay; clicking outside the panel
 *    does not close it. The user retains a clear view of the document and
 *    can interact with it (scroll, click rules) while the panel stays open.
 *  - `scroll: true` — the body scroll is not locked while the panel is open.
 *  - `keyboard: false` — the Escape key does not close the panel either.
 *    The only way to close is via the explicit close button.
 */
const OFFCANVAS_OPTIONS: Partial<Offcanvas.Options> = {
  backdrop: false,
  scroll: true,
  keyboard: false,
};

class ContradictionOffcanvas extends BaseComponent {
  private offcanvas: Offcanvas | null = null;
  private unsubscribe?: () => void;
  private lastShownResponseRef: CheckRulesResponse | null = null;
  private toastService: ToastService = ToastService.getInstance();

  protected getDefaultCssClass(): string {
    return 'contradiction-offcanvas';
  }

  protected getTranslationFallbacks(): Record<string, string> {
    return {
      'contradictions.empty': 'No conflicts found.',
      'contradictions.stmtA': 'Statement A',
      'contradictions.stmtB': 'Statement B',
      'contradictions.confidence': 'confidence',
      'contradictions.checkError':
        'The check step failed; statements are shown but conflicts could not be computed.',
      'contradictions.locateStatement': 'Show statement in document',
      'contradictions.statementNotFound':
        'Statement text not found in the loaded document.',
      'contradictions.statementsHeading': 'Statements',
      'contradictions.conflictsHeading': 'Conflicts',
      'contradictions.noStatements': 'No statements extracted.',
      // Fallbacks mirror en.json relations.conflict.* — keep in sync if params change.
      'relations.conflict.headline': '{{label}}: {{subject}} / {{property}}',
      'relations.conflict.resolved': '{{maxim}} — {{norm}} prevails',
      'relations.conflict.resolvableBy': 'May be resolved by {{maxim}}',
      'relations.conflict.scope': 'Scope: {{scope}}',
      'relations.conflict.defeatQualifier': '({{label}})',
      'contradictions.similaritiesHeading': 'Similarities',
      'contradictions.nearMissesHeading': 'Near-misses',
      'contradictions.gapsHeading': 'Coverage gaps',
      'relations.section.empty': 'None found',
      'relations.similarityMsg.headline':
        '{{label}}: {{subject}} / {{property}}',
    };
  }

  protected render(): void {
    // Markup lives in index.html; nothing to generate
  }

  protected override onInitialize(): void {
    if (!this.container) return;

    const element = this.container.querySelector<HTMLElement>('.offcanvas');
    if (!element) {
      logger.warn('ContradictionOffcanvas: offcanvas element not found');
      return;
    }
    this.offcanvas = new Offcanvas(element, OFFCANVAS_OPTIONS);

    this.unsubscribe = useAppStore.subscribe((state, prevState) => {
      if (state.lastCheckResponse !== prevState.lastCheckResponse) {
        this.handleResponseChange(state.lastCheckResponse);
      }
    });

    const current = useAppStore.getState().lastCheckResponse;
    if (current) {
      this.handleResponseChange(current);
    }

    this.container.addEventListener('click', (ev) => {
      const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>(
        '[data-locate-rule-iri]'
      );
      if (!target) return;
      ev.preventDefault();
      this.handleLocateRule(target.dataset.locateRuleIri ?? '');
    });
  }

  show(): void {
    const response = useAppStore.getState().lastCheckResponse;
    this.renderBody(response);
    this.offcanvas?.show();
  }

  hide(): void {
    this.offcanvas?.hide();
  }

  /**
   * Scroll the offcanvas body to the rule card for `iri`, expanding the
   * Rules accordion if it is collapsed (without collapsing Contradictions),
   * and flashing the card for orientation.
   *
   * Returns `true` if a matching card was found and scrolled into view.
   * Callers should invoke `show()` separately when auto-opening is desired
   * (issue #72).
   */
  scrollToRule(iri: string): boolean {
    if (!iri || !this.container) return false;

    const card = this.container.querySelector<HTMLElement>(
      `.card[data-rule-iri="${CSS.escape(iri)}"]`
    );
    if (!card) return false;

    const rulesCollapse = this.container.querySelector<HTMLElement>(
      `#${this.baseCssClass}-rules`
    );
    if (rulesCollapse && !rulesCollapse.classList.contains('show')) {
      const rulesButton = this.container.querySelector<HTMLElement>(
        `[data-bs-target="#${this.baseCssClass}-rules"]`
      );
      rulesCollapse.classList.add('show');
      rulesCollapse.classList.remove('collapse');
      rulesCollapse.classList.add('collapse');
      rulesButton?.classList.remove('collapsed');
      rulesButton?.setAttribute('aria-expanded', 'true');
    }

    card.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const scrollContainer: HTMLElement =
      card.closest('.offcanvas-body') ??
      (document.scrollingElement as HTMLElement | null) ??
      document.documentElement;

    let triggered = false;
    const startFlash = (): void => {
      if (triggered) return;
      triggered = true;
      scrollContainer.removeEventListener('scrollend', startFlash);
      window.clearTimeout(fallback);
      card.classList.remove('rule-flash');
      void card.offsetWidth; // force reflow so the animation restarts
      card.classList.add('rule-flash');
      window.setTimeout(() => card.classList.remove('rule-flash'), 1200);
    };

    scrollContainer.addEventListener('scrollend', startFlash, { once: true });
    const fallback: number = window.setTimeout(startFlash, 600);

    return true;
  }

  override updateUILanguage(): void {
    if (useAppStore.getState().lastCheckResponse) {
      this.renderBody(useAppStore.getState().lastCheckResponse);
    }
  }

  override destroy(): void {
    this.unsubscribe?.();
    this.offcanvas?.dispose();
    super.destroy();
  }

  // ============== private =================

  private handleLocateRule(iri: string): void {
    if (!iri) return;
    const response = useAppStore.getState().lastCheckResponse;
    const stmt = response?.statements.find((s) => s.iri === iri);
    if (!stmt) return;
    const found = findAndFlashStatement(stmt);
    if (!found) {
      this.toastService.showWarning(this.t('contradictions.statementNotFound'));
    }
  }

  private handleResponseChange(response: CheckRulesResponse | null): void {
    if (!response) return;
    if (response === this.lastShownResponseRef) return;
    this.lastShownResponseRef = response;
    this.renderBody(response);
    if ((response.statements?.length ?? 0) > 0) {
      this.offcanvas?.show();
    }
  }

  private renderBody(response: CheckRulesResponse | null): void {
    const body = this.container?.querySelector<HTMLElement>(
      `#${this.baseCssClass}-element-body`
    );
    if (!body) return;

    if (!response) {
      body.innerHTML = `<p class="text-muted small">${escapeHtml(this.t('contradictions.empty'))}</p>`;
      return;
    }

    const stmtByIri = new Map<string, StatementDisplay>();
    for (const s of response.statements) stmtByIri.set(s.iri, s);

    const errorBanner = response.check_error
      ? `<div class="alert alert-warning small mb-3">${escapeHtml(this.t('contradictions.checkError'))}<br><code>${escapeHtml(response.check_error)}</code></div>`
      : '';

    body.innerHTML = `${errorBanner}${this.renderSections(response, stmtByIri)}`;
  }

  private renderSections(
    response: CheckRulesResponse,
    stmtByIri: Map<string, StatementDisplay>
  ): string {
    const statements = response.statements;
    const conflicts = response.conflicts ?? [];
    const similarities = response.similarities ?? [];
    const nearMisses = response.near_misses ?? [];
    const gaps = response.gaps ?? [];

    const ctx: MessageContext = {
      t: (key, params) => this.t(key, params),
      resolveLabel: createResolveLabel(
        (key, params) => this.t(key, params),
        interactionLabels
      ),
      statements,
    };

    const hasConflicts = conflicts.length > 0;
    const sectionEmpty = `<p class="text-muted small mb-0">${escapeHtml(this.t('relations.section.empty'))}</p>`;

    const conflictsBody =
      conflicts.length === 0
        ? `<p class="text-muted small mb-0">${escapeHtml(this.t('contradictions.empty'))}</p>`
        : conflicts
            .map((c) =>
              this.renderConflictCard(
                c,
                stmtByIri.get(c.rule_a),
                stmtByIri.get(c.rule_b),
                ctx
              )
            )
            .join('');

    const similaritiesBody =
      similarities.length === 0
        ? sectionEmpty
        : similarities
            .map((s) =>
              this.renderMessageCard(
                buildSimilarityMessage(s, ctx),
                s.confidence
              )
            )
            .join('');

    const nearMissesBody =
      nearMisses.length === 0
        ? sectionEmpty
        : nearMisses.map((nm) => this.renderNearMissCard(nm)).join('');

    const gapsBody =
      gaps.length === 0
        ? sectionEmpty
        : gaps
            .map((g) =>
              this.renderMessageCard(buildGapMessage(g, ctx), g.confidence)
            )
            .join('');

    const statementsBody =
      statements.length === 0
        ? `<p class="text-muted small mb-0">${escapeHtml(this.t('contradictions.noStatements'))}</p>`
        : statements.map((s) => this.renderStatementCard(s)).join('');

    return [
      this.renderAccordionItem(
        'contradictions',
        this.t('contradictions.conflictsHeading'),
        conflicts.length,
        conflictsBody,
        hasConflicts
      ),
      this.renderAccordionItem(
        'similarities',
        this.t('contradictions.similaritiesHeading'),
        similarities.length,
        similaritiesBody,
        false
      ),
      this.renderAccordionItem(
        'near-misses',
        this.t('contradictions.nearMissesHeading'),
        nearMisses.length,
        nearMissesBody,
        false
      ),
      this.renderAccordionItem(
        'gaps',
        this.t('contradictions.gapsHeading'),
        gaps.length,
        gapsBody,
        false
      ),
      this.renderAccordionItem(
        'rules',
        this.t('contradictions.statementsHeading'),
        statements.length,
        statementsBody,
        !hasConflicts,
        true
      ),
    ].join('');
  }

  private renderAccordionItem(
    key: string,
    title: string,
    count: number,
    bodyHtml: string,
    open: boolean,
    last: boolean = false
  ): string {
    const id = `${this.baseCssClass}-${key}`;
    return `
      <div class="accordion${last ? '' : ' mb-2'}">
        <div class="accordion-item">
          <h2 class="accordion-header">
            <button
              class="accordion-button${open ? '' : ' collapsed'}"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#${id}"
              aria-expanded="${open}"
              aria-controls="${id}"
            >
              ${escapeHtml(title)} (${count})
            </button>
          </h2>
          <div id="${id}" class="accordion-collapse collapse${open ? ' show' : ''}">
            <div class="accordion-body p-2">${bodyHtml}</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderMessageCard(
    msg: InteractionMessage,
    confidence: number
  ): string {
    const qualifier = msg.qualifier
      ? ` <span class="text-muted">${escapeHtml(msg.qualifier)}</span>`
      : '';
    const subtitle = msg.subtitle
      ? `<div class="text-muted" style="font-size: 0.8em;">${escapeHtml(msg.subtitle)}</div>`
      : '';
    const detail = msg.detail
      ? `<div class="text-muted" style="font-size: 0.8em;">${escapeHtml(msg.detail)}</div>`
      : '';
    return `
      <div class="card card-body py-2 px-3 mb-1 small">
        <div>${escapeHtml(msg.headline)}${qualifier}</div>
        ${subtitle}
        <div class="text-muted" style="font-size: 0.8em;">${escapeHtml(this.t('contradictions.confidence'))} ${(confidence * 100).toFixed(0)}%</div>
        ${detail}
      </div>
    `;
  }

  private renderNearMissCard(nm: NearMissDisplay): string {
    const reason = nm.miss_reason
      ? `<div class="text-muted" style="font-size: 0.8em;">${escapeHtml(nm.miss_reason)}</div>`
      : '';
    return `
      <div class="card card-body py-2 px-3 mb-1 small">
        <div>${escapeHtml(nm.source_sentence)}</div>
        ${reason}
      </div>
    `;
  }

  private renderStatementCard(stmt: StatementDisplay): string {
    const stmtNumber = useAppStore.getState().statementNumberMap.get(stmt.iri);
    const numberLabel = stmtNumber ? `#${stmtNumber}` : '';
    const badgeClass = this.deonticBadgeClass(stmt.deontic_category);
    const categoryLabel = stmt.deontic_category ?? '';
    const sourceText = this.highlightSubject(
      stmt.source_sentence,
      stmt.subject
    );
    const locateTitle = escapeHtml(this.t('contradictions.locateStatement'));
    const stmtIri = escapeHtml(stmt.iri);
    const detailRows = this.renderDetailRows(stmt);

    return `
      <div class="card mb-2" data-rule-iri="${stmtIri}">
        <div class="card-body py-2 px-3 small">
          <div class="d-flex align-items-start gap-2 mb-1">
            ${numberLabel ? `<span class="badge text-bg-light text-muted fw-normal flex-shrink-0">${numberLabel}</span>` : ''}
            <button
              type="button"
              class="btn btn-link p-0 text-start text-decoration-none d-block h-100 w-100"
              data-locate-rule-iri="${stmtIri}"
              title="${locateTitle}"
            >${sourceText}</button>
          </div>
          ${categoryLabel ? `<div class="d-flex flex-wrap gap-1 mt-1"><span class="badge ${badgeClass}">${escapeHtml(categoryLabel)}</span>${stmt.modal_strength ? `<span class="badge text-bg-secondary">${escapeHtml(stmt.modal_strength)}</span>` : ''}</div>` : ''}
          ${detailRows}
        </div>
      </div>
    `;
  }

  private renderDetailRows(stmt: StatementDisplay): string {
    const rows: string[] = [];
    const addRow = (label: string, value: string | null | undefined): void => {
      if (value)
        rows.push(
          `<tr><td class="text-muted pe-2">${escapeHtml(label)}</td><td>${escapeHtml(value)}</td></tr>`
        );
    };

    addRow('Subject', stmt.subject);
    if (stmt.conditions?.length)
      addRow('Conditions', stmt.conditions.join(', '));
    addRow('When', stmt.antecedent);
    addRow('Property', stmt.property);

    if (stmt.comparator || stmt.value || stmt.unit) {
      addRow(
        'Requirement',
        formatRequirement(stmt.comparator, stmt.value, stmt.unit)
      );
    }

    addRow('Quality', stmt.quality);
    addRow('Action', stmt.action);
    addRow('Object', stmt.object);
    addRow('Recipient', stmt.recipient);
    addRow('Refers to', stmt.reference);

    if (stmt.definiendum || stmt.definiens) {
      addRow(
        'Defines',
        [stmt.definiendum, stmt.definiens].filter(Boolean).join(' — ')
      );
    }

    addRow('Assessment', stmt.evaluative_content);

    if (rows.length === 0) return '';
    return `<table class="table table-sm table-borderless mb-0 mt-1" style="font-size: 0.85em">${rows.join('')}</table>`;
  }

  private highlightSubject(
    text: string,
    subject: string | null | undefined
  ): string {
    if (!subject || !text) return escapeHtml(text ?? '');
    const escaped = escapeHtml(text);
    const subjectEscaped = escapeHtml(subject);
    const idx = escaped.toLowerCase().indexOf(subjectEscaped.toLowerCase());
    if (idx === -1) return escaped;
    const before = escaped.substring(0, idx);
    const match = escaped.substring(idx, idx + subjectEscaped.length);
    const after = escaped.substring(idx + subjectEscaped.length);
    return `${before}<strong>${match}</strong>${after}`;
  }

  private renderConflictCard(
    c: ConflictDisplay,
    stmtA: StatementDisplay | undefined,
    stmtB: StatementDisplay | undefined,
    ctx: MessageContext
  ): string {
    const a = stmtA ?? this.placeholderStatement(c.rule_a);
    const b = stmtB ?? this.placeholderStatement(c.rule_b);

    const numberMap = useAppStore.getState().statementNumberMap;
    const numA = numberMap.get(c.rule_a);
    const numB = numberMap.get(c.rule_b);
    const labelA = numA ? `Statement #${numA}` : this.t('contradictions.stmtA');
    const labelB = numB ? `Statement #${numB}` : this.t('contradictions.stmtB');

    const msg = buildConflictMessage(c, ctx);

    const typeBadge = `<span class="badge bg-warning text-dark">${escapeHtml(msg.headline)}</span>`;
    const qualifier = msg.qualifier
      ? `<span class="small text-muted">${escapeHtml(msg.qualifier)}</span>`
      : '';
    const subtitle = msg.subtitle
      ? `<div class="small text-muted w-100">${escapeHtml(msg.subtitle)}</div>`
      : '';
    const confidence = `${(c.confidence * 100).toFixed(0)}%`;
    const detailText = msg.detail ?? c.explanation ?? '';
    const explanation = detailText
      ? `<div class="card-footer small text-muted">${escapeHtml(detailText)}</div>`
      : '';

    return `
      <div class="card mb-3">
        <div class="card-header py-2 px-3 d-flex flex-wrap gap-2 align-items-center bg-light">
          ${typeBadge}
          ${qualifier}
          <span class="ms-auto small text-muted">${escapeHtml(this.t('contradictions.confidence'))} ${confidence}</span>
          ${subtitle}
        </div>
        <div class="row g-0">
          <div class="col-6 border-end p-3 small">
            ${this.renderStatementColumn(labelA, a, b)}
          </div>
          <div class="col-6 p-3 small">
            ${this.renderStatementColumn(labelB, b, a)}
          </div>
        </div>
        ${explanation}
      </div>
    `;
  }

  private renderStatementColumn(
    label: string,
    stmt: StatementDisplay,
    other: StatementDisplay
  ): string {
    const diff = (a: string | null | undefined, b: string | null | undefined) =>
      (a ?? '') !== (b ?? '');

    const sourceText = this.highlightSubject(
      stmt.source_sentence,
      stmt.subject
    );
    const locateTitle = escapeHtml(this.t('contradictions.locateStatement'));
    const stmtIri = escapeHtml(stmt.iri);

    const categoryLabel = stmt.deontic_category ?? '';
    const badgeClass = this.deonticBadgeClass(stmt.deontic_category);
    const modalDiffers = diff(stmt.modal_strength, other.modal_strength);

    const diffFields = this.renderDiffDetailRows(stmt, other);

    return `
      <div class="text-uppercase fw-bold text-muted mb-2 rule-column-label">${escapeHtml(label)}</div>
      <button
        type="button"
        class="btn btn-link p-0 text-start text-decoration-none mb-2 d-block w-100"
        data-locate-rule-iri="${stmtIri}"
        title="${locateTitle}"
      >${sourceText}</button>
      <div class="d-flex flex-wrap gap-1 mb-2">
        ${stmt.modal_strength ? this.fieldBadge(stmt.modal_strength, modalDiffers) : ''}
        ${categoryLabel ? `<span class="badge ${badgeClass}">${escapeHtml(categoryLabel)}</span>` : ''}
      </div>
      ${diffFields}
    `;
  }

  private renderDiffDetailRows(
    stmt: StatementDisplay,
    other: StatementDisplay
  ): string {
    const diff = (a: string | null | undefined, b: string | null | undefined) =>
      (a ?? '') !== (b ?? '');

    const rows: string[] = [];

    const addRow = (
      label: string,
      val: string | null | undefined,
      otherVal: string | null | undefined
    ): void => {
      if (!val) return;
      const differs = diff(val, otherVal);
      const cls = differs ? 'rule-value rule-value--differs' : 'rule-value';
      rows.push(
        `<div class="mb-1"><span class="text-muted">${escapeHtml(label)}:</span> <span class="${cls}">${escapeHtml(val)}</span></div>`
      );
    };

    addRow('Property', stmt.property, other.property);

    if (stmt.comparator || stmt.value || stmt.unit) {
      const val = formatRequirement(stmt.comparator, stmt.value, stmt.unit);
      const otherVal = formatRequirement(
        other.comparator,
        other.value,
        other.unit
      );
      const differs = val !== otherVal;
      const cls = differs ? 'rule-value rule-value--differs' : 'rule-value';
      rows.push(
        `<div class="mb-1"><span class="text-muted">Requirement:</span> <span class="${cls}">${escapeHtml(val)}</span></div>`
      );
    }

    addRow('Quality', stmt.quality, other.quality);
    addRow('Action', stmt.action, other.action);
    addRow('Object', stmt.object, other.object);
    addRow('Recipient', stmt.recipient, other.recipient);
    addRow('Refers to', stmt.reference, other.reference);

    if (stmt.definiendum || stmt.definiens) {
      const val = [stmt.definiendum, stmt.definiens]
        .filter(Boolean)
        .join(' — ');
      const otherDef = [other.definiendum, other.definiens]
        .filter(Boolean)
        .join(' — ');
      const differs = val !== otherDef;
      const cls = differs ? 'rule-value rule-value--differs' : 'rule-value';
      rows.push(
        `<div class="mb-1"><span class="text-muted">Defines:</span> <span class="${cls}">${escapeHtml(val)}</span></div>`
      );
    }

    addRow('Assessment', stmt.evaluative_content, other.evaluative_content);

    return rows.join('');
  }

  private fieldBadge(label: string, differs: boolean): string {
    if (!label) return '';
    const cls = differs ? 'badge text-bg-warning' : 'badge text-bg-secondary';
    return `<span class="${cls}">${escapeHtml(label)}</span>`;
  }

  private deonticBadgeClass(
    deonticCategory: string | null | undefined
  ): string {
    if (!deonticCategory) return 'text-bg-secondary';
    if (deonticCategory === 'obligation') return 'text-bg-primary';
    if (deonticCategory === 'prohibition') return 'text-bg-danger';
    if (deonticCategory === 'permission') return 'text-bg-success';
    if (deonticCategory === 'definition') return 'text-bg-info';
    return 'text-bg-secondary';
  }

  private placeholderStatement(iri: string): StatementDisplay {
    return {
      iri,
      source_sentence: `(statement ${iri.split('/').pop()} not found)`,
      deontic_category: 'obligation',
    };
  }
}

export default ContradictionOffcanvas;
